/* =========================================================================
 * Kkumi · Wishlist Recommendations ("based on your loves")
 * Personalizes the row from the customer's wishlist:
 *   1) GET /wishlists/mine (WishlistKing) -> saved productIds
 *   2) For each saved product, GET /recommendations/products.json?intent=related
 *   3) Aggregate, dedupe, drop already-saved, render the top N cards
 * "Based on wishlist" and "Others also saved" are about the viewer's own
 * saves, so outside a shared list they show only once the viewer is known to
 * have saved something: a guest, an empty list or a non-member while the
 * wishlist is for Club members only never sees them (the section ships hidden,
 * data-kkwl-needs-list, and ownList() decides). When Shopify's related
 * products leave the row short (none, or fewer than it shows), Kkumi Oracle
 * fills the rest (oracleTopUp: what other customers bought with the saved
 * items, then the shop's best picks; owner, 29 Oct 2026). The server-rendered
 * manual collection is the fallback when neither has anything, and fills the
 * theme editor.
 * ========================================================================= */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-kkwl-recs]');
  if (!roots.length) return;

  var designMode = !!(window.Shopify && window.Shopify.designMode);
  var SIGNED_IN = !!(window.__kkWishCfg && window.__kkWishCfg.loggedIn);
  var MONEY_FORMAT = roots[0].getAttribute('data-kkwl-money-format') || '${{amount}}';
  // Merchant-editable copy (data-* on the section root); literals are the fallback.
  // The cards' own copy is the sitewide Theme settings → Kkumi product cards (KKCard).
  var SHARED_NAME_FALLBACK = roots[0].getAttribute('data-kkwl-shared-name-fallback') || 'a friend';
  var SEED_CAP = 16;      // max wishlist items used as recommendation seeds
  var PER_SEED = 10;      // recommendations fetched per seed (Shopify's max for intent=related)

  var esc = window.KKEsc; // canonical shared escaper (assets/kkumi-esc.js)

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision; thousands = thousands || ','; decimal = decimal || '.';
    if (isNaN(cents) || cents == null) return '0';
    cents = (cents / 100).toFixed(precision);
    var parts = cents.split('.');
    return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands) + (parts[1] ? decimal + parts[1] : '');
  }
  function moneyFromCents(cents) { // recommendations API prices are already in cents
    cents = Number(cents);
    if (isNaN(cents)) return '';
    var ph = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
    var token = ph ? ph[1] : 'amount';
    var val;
    switch (token) {
      case 'amount_no_decimals': val = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': val = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': val = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': val = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_apostrophe_separator': val = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: val = formatWithDelimiters(cents, 2);
    }
    return ph ? MONEY_FORMAT.replace(ph[0], val) : MONEY_FORMAT + val;
  }
  function imgUrl(url, w) { if (!url) return ''; return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w; }
  function rootUrl() { return (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/'; }

  // ----- "See more like this" button, resolved from what's actually shown -----
  // Most common product type across the rendered cards; '' when none carry a type.
  function dominantType(products) {
    var counts = {}, best = '', bestN = 0;
    for (var i = 0; i < products.length; i++) {
      var t = (products[i] && products[i].type ? String(products[i].type) : '').trim();
      if (!t) continue;
      counts[t] = (counts[t] || 0) + 1;
      if (counts[t] > bestN) { bestN = counts[t]; best = t; }
    }
    return best;
  }
  // Point the button (Auto mode) at a search for that dominant category, falling
  // back to the manual URL, then the fallback collection. No-op when the button is
  // absent or was already resolved server-side (manual mode / collection source).
  function resolveMoreButton(root, products) {
    var btn = root.querySelector('[data-kkwl-more]');
    if (!btn || !btn.hasAttribute('hidden')) return;
    var auto = (root.getAttribute('data-kkwl-more-mode') || 'auto') === 'auto';
    var href = '';
    if (auto && products && products.length) {
      var type = dominantType(products);
      // encodeURIComponent-only value into a URL, set via setAttribute (no HTML parse).
      if (type) href = rootUrl() + 'search?q=' + encodeURIComponent(type) + '&type=product';
    }
    // Manual mode honours only the manual URL; Auto also falls back to the collection.
    if (!href) href = root.getAttribute('data-kkwl-more-fallback') || '';
    if (!href && auto) href = root.getAttribute('data-kkwl-more-collection') || '';
    if (href) { btn.setAttribute('href', href); btn.removeAttribute('hidden'); }
  }

  var waitForWK = window.KKWaitForWK; // shared poller (assets/kkumi-wishlist-king.js)

  // recommendations.json has no metafields, so batch-fetch reviews + the
  // product-card metafields (card title, pre-order) via Storefront
  // GraphQL. A metafield the storefront can't read comes back null.
  function fetchRatings(ids) {
    return new Promise(function (resolve) {
      if (!ids.length) { resolve({}); return; }
      var go = function (WK) {
        var cfg = (WK && WK.config && WK.config.storefront) || {};
        var token = cfg.accessToken;
        var ver = cfg.version || '2024-10';
        if (!token) { resolve({}); return; }
        var gids = ids.map(function (id) { return 'gid://shopify/Product/' + id; });
        var query = '{ nodes(ids: ' + JSON.stringify(gids) + ') { ... on Product { id ' +
          'ratingMeta: metafield(namespace: "reviews", key: "rating") { value } ' +
          'ratingCountMeta: metafield(namespace: "reviews", key: "rating_count") { value } ' +
          'cardTitleMeta: metafield(namespace: "custom", key: "card_title") { value } ' +
          'preorderMeta: metafield(namespace: "kkumi", key: "preorder") { value } ' +
          'preorderDateMeta: metafield(namespace: "kkumi", key: "preorder_ship_date") { value } ' +
          'preorderNoteMeta: metafield(namespace: "kkumi", key: "preorder_note") { value } } } }';
        fetch('/api/' + ver + '/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({ query: query })
        }).then(function (r) { return r.json(); }).then(function (res) {
          var map = {}, nodes = (res && res.data && res.data.nodes) || [];
          nodes.forEach(function (n) {
            if (!n || !n.id) return;
            var m = String(n.id).match(/(\d+)\s*$/);
            var key = m ? m[1] : n.id;
            var count = (n.ratingCountMeta && parseInt(n.ratingCountMeta.value, 10)) || 0;
            var value = 0;
            if (count > 0 && n.ratingMeta && n.ratingMeta.value) {
              try { value = parseFloat(JSON.parse(n.ratingMeta.value).value) || 0; }
              catch (e) { value = parseFloat(n.ratingMeta.value) || 0; }
            }
            var mv = function (f) { return f && f.value != null ? String(f.value) : ''; };
            map[key] = {
              value: value,
              count: count,
              cardTitle: mv(n.cardTitleMeta),
              preorder: mv(n.preorderMeta) === 'true',
              preorderDate: mv(n.preorderDateMeta),
              preorderNote: mv(n.preorderNoteMeta)
            };
          });
          resolve(map);
        }).catch(function () { resolve({}); });
      };
      waitForWK(go, function () { resolve({}); });
    });
  }

  // kkumi.preorder_ship_date ("2026-11-14") → "14 Nov", same as the Liquid '%-d %b'.
  function shipDate(v) {
    if (!/^\d{4}-\d{2}-\d{2}/.test(v || '')) return '';
    var d = new Date(v.slice(0, 10) + 'T00:00:00Z');
    if (isNaN(d)) return '';
    try { return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch (e) { return ''; }
  }
  var HEART_ICON = '<svg class="kkumi-card__wish-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  // The sitewide product card (KKCard), built from a recommendations.json
  // product (prices in cents) — same rules + markup as the Liquid card.
  function recCard(p) {
    var K = window.KKCard;
    var C = K.copy;
    var meta = p._rating || { value: 0, count: 0 };
    var variants = p.variants || [];
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    // Every option, in order ({ name, values }); recommendations.json may give
    // bare names, so values are then collected from the variants.
    var variantVals = function (v) { return v.options || [v.option1, v.option2, v.option3].filter(function (x) { return x != null; }); };
    var options = (p.options || []).map(function (o, i) {
      var name = o && typeof o === 'object' ? o.name : o;
      var values = o && typeof o === 'object' && o.values ? o.values
        : variants.map(function (v) { return variantVals(v)[i]; }).filter(function (x, j, a) { return x && a.indexOf(x) === j; });
      return { name: name, values: values };
    }).filter(function (o) { return o.name && o.name !== 'Title'; });
    var vid = (variants.filter(function (v) { return v.available; })[0] || variants[0] || {}).id || '';
    var s = K.state({
      title: p.title,
      cardTitle: meta.cardTitle,
      type: p.type || '',
      available: p.available !== false,
      isPreorder: !!meta.preorder,
      preorderDate: shipDate(meta.preorderDate),
      preorderNote: meta.preorderNote,
      isBundle: variants.some(function (v) { return v.requires_components; }),
      tags: tags,
      priceVaries: !!p.price_varies,
      options: options,
      variants: variants.map(function (v) {
        return { available: v.available !== false, price: Number(v.price) || 0, compareAt: Number(v.compare_at_price) || 0, options: variantVals(v) };
      })
    }, { money: moneyFromCents });

    var title = String(p.title || '');
    // The wishlist heart is for signed-in customers only, as on the Liquid card
    // (snippets/kkumi-product-card-v3.liquid): saving needs an account.
    var heart = !SIGNED_IN ? '' : '<button type="button" class="kk-pc__heart kkumi-card__wish wk-loading" data-product-handle="' + esc(p.handle) + '" data-product-id="' + esc(p.id) + '" data-variant-id="' + esc(vid) + '"' +
      ' data-label-add="' + esc(C.wishAdd + ': ' + title) + '" data-label-remove="' + esc(C.wishRemove + ': ' + title) + '"' +
      ' aria-pressed="false" aria-label="' + esc(C.wishAdd + ': ' + title) + '">' +
      '<span class="kk-pc__wish-spinner" aria-hidden="true"></span>' + HEART_ICON + '</button>';

    // One button: sold out → the PDP (its notify form); a choice to make or a
    // pre-order → the PDP; one variant → AJAX add via the global cart/add
    // handler in global.js (KKBtn busy state).
    var cta;
    if (p.available === false) cta = K.button({ label: C.ctaNotify, href: p.url, secondary: true });
    else if (meta.preorder) cta = K.button({ label: C.ctaPreorder, href: p.url });
    else if (variants.length > 1 || !variants.every(function (v) { return v.physical === true; })) cta = K.button({ label: C.ctaOptions, href: p.url });
    else {
      cta = '<form method="post" action="' + esc(rootUrl() + 'cart/add') + '" class="kk-pc__form">' +
        '<input type="hidden" name="id" value="' + esc(vid) + '">' +
        K.button({ label: C.ctaAdd, submit: true, attrs: { 'data-busy-label': C.ctaAdding, 'aria-label': C.ctaAdd + ': ' + title } }) +
        '</form>';
    }
    var hoverImg = (p.images && p.images.length > 1) ? p.images[1] : null;
    var edition = K.editionFromTags(tags);
    return K.html({
      url: p.url,
      title: s.title,
      linkTitle: title,
      imgSrc: p.featured_image ? imgUrl(p.featured_image, 600) : '',
      imgAlt: title,
      imgHover: hoverImg ? imgUrl(hoverImg, 600) : '',
      heartHTML: heart,
      tag: s.tag,
      tagSale: s.tagSale,
      badge: s.badge,
      edition: edition.label,
      editionStyle: edition.style,
      ratingValue: meta.value,
      ratingCount: meta.count,
      note: s.note,
      notePre: s.notePre,
      priceLabel: s.priceLabel,
      priceAmount: s.priceAmount,
      priceSale: s.priceSale,
      priceCompare: s.priceCompare,
      priceSave: s.priceSave,
      ctaHTML: cta,
      oos: p.available === false,
      rootAttrs: {
        'data-product-id': p.id,
        'data-available': p.available === false ? 'false' : 'true',
        'data-on-sale': s.onSale ? 'true' : 'false'
      }
    });
  }

  // The viewer's own saves (a personalised source outside a shared list). The section shows only when there are
  // some; nothing saved, no answer, or the app saying the wishlist is Club-only and they aren't in the Club keeps it
  // hidden for good, fallback collection or not, and so does the list emptying or the Club answer changing later
  // on this page. cb(items, wishlist) runs once, after the section is revealed.
  function hideForGood(root) {
    var grid = root.querySelector('.kkwl-recs__grid');
    if (grid) grid.innerHTML = '';
    root.removeAttribute('data-kkwl-ready');
    root.style.display = 'none';
  }
  function clubBlocks(club) { return !!(club && club.required && !club.member); }
  function ownList(root, cb) {
    var gone = false;
    var drop = function () { if (!gone) { gone = true; hideForGood(root); } };
    // The list emptied after it was shown. Only then: the wishlist core also announces its cached count at page
    // load, which is 0 on a device that hasn't read the list yet, before the read below has answered.
    document.addEventListener('wk:countUpdated', function (e) {
      var d = e && e.detail;
      if (root.hasAttribute('data-kkwl-ready') && d && d.source === 'kkwl' && Number(d.count) === 0) drop();
    });
    document.addEventListener('wk:clubUpdated', function (e) { if (clubBlocks(e && e.detail && e.detail.club)) drop(); });
    if (!SIGNED_IN) { drop(); return; }
    waitForWK(function (WK) {
      fetch(WK.config.routes.apiHost + '/wishlists/mine', { headers: WK._apis.wishlist.headers })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var wl = (d && d.wishlist) || {};
          var items = wl.items || [];
          if (gone) return;
          if (!items.length || clubBlocks(d && d.club)) { drop(); return; }
          root.setAttribute('data-kkwl-ready', '');
          cb(items, wl);
        })
        .catch(drop);
    }, drop);
  }

  // When personalisation can't show anything AND no server fallback collection is
  // set, clear the skeleton and hide the section so it doesn't sit empty/loading.
  function clearOrHide(root) {
    if (!root || root.getAttribute('data-kkwl-has-fallback') === 'true') {
      // Server-rendered fallback collection cards stay on screen — resolve the
      // "see more" button against them (its manual/collection fallback URLs).
      if (root) resolveMoreButton(root, []);
      return;
    }
    var grid = root.querySelector('.kkwl-recs__grid');
    if (grid) grid.innerHTML = '';
    root.style.display = 'none';
  }

  // Tell kkumi-wishlist.js that new wishlist hearts have just appeared, so it
  // re-runs syncButtons() (paints wk-active on saved items) + clearInitialLoading()
  // (drops the wk-loading spinner). The cards otherwise render after the initial
  // page-load sync ran, so they'd stay stuck on the loading spinner forever.
  function paintHearts() {
    document.dispatchEvent(new CustomEvent('kkumi:collection:updated'));
  }

  // Attach review ratings (batched) then render, so the hearts show on first paint.
  function renderWithRatings(root, products, count) {
    var grid = root.querySelector('.kkwl-recs__grid');
    if (!grid || !products.length) return false;
    var list = products.slice(0, count);
    // Shopify's related products carry no metafields; Oracle's picks (resolveWorker) arrive with theirs.
    var bare = list.filter(function (p) { return !p._rating; });
    fetchRatings(bare.map(function (p) { return p.id; })).then(function (ratings) {
      bare.forEach(function (p) { p._rating = ratings[p.id] || { value: 0, count: 0 }; });
      grid.innerHTML = list.map(recCard).join('');
      paintHearts();
      resolveMoreButton(root, list);
    });
    return true;
  }

  function fetchRecs(seedId) {
    return fetch(rootUrl() + 'recommendations/products.json?product_id=' + seedId + '&intent=related&limit=' + PER_SEED)
      .then(function (r) { return r.json(); })
      .then(function (d) { return (d && d.products) || []; })
      .catch(function () { return []; });
  }

  // Aggregate Shopify recommendations seeded by a set of wishlist items; when they leave the row short, Oracle's
  // picks for the list (publicId) fill the rest.
  function seedAndRender(root, count, items, publicId) {
    if (!items || !items.length) { clearOrHide(root); return; }
    var have = {};
    items.forEach(function (i) { have[i.productId] = true; });
    var seeds = items.slice(0, SEED_CAP).map(function (i) { return i.productId; });
    Promise.all(seeds.map(fetchRecs)).then(function (lists) {
      var seen = {}, out = [];
      lists.forEach(function (list) {
        list.forEach(function (p) {
          if (!p || !p.id || have[p.id] || seen[p.id]) return;
          seen[p.id] = true; out.push(p);
        });
      });
      if (out.length >= count) { renderWithRatings(root, out, count); return; }
      return oracleTopUp(publicId, count - out.length, function (p) { return !have[p.id] && !seen[p.id]; }).then(function (more) {
        if (!renderWithRatings(root, out.concat(more), count)) clearOrHide(root);
      });
    }).catch(function () { clearOrHide(root); });
  }

  // ----- Worker-backed discovery (trending + "others also saved") -----
  // The worker returns ranked product-ids only; resolve them to recCard-shaped
  // objects via the Storefront API (ranked order preserved, ratings included).
  function resolveWorker(WK, ids) {
    return new Promise(function (resolve) {
      var cfg = (WK && WK.config && WK.config.storefront) || {};
      var token = cfg.accessToken; var ver = cfg.version || '2024-10';
      if (!token || !ids.length) { resolve([]); return; }
      var gids = ids.map(function (id) { return 'gid://shopify/Product/' + id; });
      var query = '{ nodes(ids: ' + JSON.stringify(gids) + ') { ... on Product { ' +
        'id title handle onlineStoreUrl productType tags availableForSale ' +
        'featuredImage { url altText } images(first: 2) { nodes { url } } ' +
        'options(first: 3) { name values } ' +
        'variants(first: 100) { nodes { id availableForSale currentlyNotInStock price { amount } compareAtPrice { amount } selectedOptions { value } } } ' +
        'ratingMeta: metafield(namespace: "reviews", key: "rating") { value } ' +
        'ratingCountMeta: metafield(namespace: "reviews", key: "rating_count") { value } ' +
        'cardTitleMeta: metafield(namespace: "custom", key: "card_title") { value } ' +
        'preorderMeta: metafield(namespace: "kkumi", key: "preorder") { value } ' +
        'preorderDateMeta: metafield(namespace: "kkumi", key: "preorder_ship_date") { value } ' +
        'preorderNoteMeta: metafield(namespace: "kkumi", key: "preorder_note") { value } ' +
        'priceRange { minVariantPrice { amount } maxVariantPrice { amount } } ' +
        'compareAtPriceRange { minVariantPrice { amount } } } } }';
      fetch('/api/' + ver + '/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query: query })
      }).then(function (r) { return r.json(); }).then(function (res) {
        var byId = {}, nodes = (res && res.data && res.data.nodes) || [];
        nodes.forEach(function (n) {
          if (!n || !n.id) return;
          var pid = (String(n.id).match(/(\d+)\s*$/) || [])[1] || n.id;
          var amt = parseFloat(n.priceRange && n.priceRange.minVariantPrice && n.priceRange.minVariantPrice.amount) || 0;
          var cmp = parseFloat(n.compareAtPriceRange && n.compareAtPriceRange.minVariantPrice && n.compareAtPriceRange.minVariantPrice.amount) || 0;
          var max = parseFloat(n.priceRange && n.priceRange.maxVariantPrice && n.priceRange.maxVariantPrice.amount) || 0;
          var toCents = function (m) { return m && m.amount != null ? Math.round(parseFloat(m.amount) * 100) || 0 : 0; };
          var mv = function (f) { return f && f.value != null ? String(f.value) : ''; };
          // Same shape as a recommendations.json variant (prices in cents).
          var variants = ((n.variants && n.variants.nodes) || []).map(function (vn) {
            return {
              id: (String(vn.id).match(/(\d+)\s*$/) || [])[1] || '',
              available: vn.availableForSale !== false,
              physical: vn.currentlyNotInStock === false,
              price: toCents(vn.price),
              compare_at_price: toCents(vn.compareAtPrice) || null,
              options: (vn.selectedOptions || []).map(function (so) { return so.value; })
            };
          });
          var imgs = ((n.images && n.images.nodes) || []).map(function (m) { return m.url; });
          var count = (n.ratingCountMeta && parseInt(n.ratingCountMeta.value, 10)) || 0;
          var value = 0;
          if (count > 0 && n.ratingMeta && n.ratingMeta.value) {
            try { value = parseFloat(JSON.parse(n.ratingMeta.value).value) || 0; }
            catch (e) { value = parseFloat(n.ratingMeta.value) || 0; }
          }
          byId[pid] = {
            id: pid, title: n.title || 'Product', handle: n.handle || '',
            url: n.onlineStoreUrl || (n.handle ? '/products/' + n.handle : '#'),
            type: n.productType || '', tags: n.tags || [],
            featured_image: n.featuredImage && n.featuredImage.url, images: imgs,
            available: n.availableForSale !== false,
            options: n.options || [],
            variants: variants,
            price: Math.round(amt * 100),
            price_varies: max > amt,
            compare_at_price: cmp > amt ? Math.round(cmp * 100) : null,
            _rating: {
              value: value,
              count: count,
              cardTitle: mv(n.cardTitleMeta),
              preorder: mv(n.preorderMeta) === 'true',
              preorderDate: mv(n.preorderDateMeta),
              preorderNote: mv(n.preorderNoteMeta)
            }
          };
        });
        resolve(ids.map(function (id) { return byId[String(id)]; }).filter(Boolean));
      }).catch(function () { resolve([]); });
    });
  }

  // GET a worker discovery endpoint -> array of product-id strings.
  function fetchWorkerIds(WK, path) {
    return fetch(WK.config.routes.apiHost + path, { headers: WK._apis.wishlist.headers })
      .then(function (r) { return r.json(); })
      .then(function (d) { return ((d && d.products) || []).map(function (p) { return String(p.productId); }); })
      .catch(function () { return []; });
  }

  // Kkumi Oracle's picks for a list (the wishlist worker's GET /wishlists/:publicId/picks), as cards: up to `need`
  // in-stock products that `keep` accepts, best first. [] without a list id, an answer or anything to show.
  function oracleTopUp(publicId, need, keep) {
    return new Promise(function (resolve) {
      if (!publicId || need <= 0) { resolve([]); return; }
      waitForWK(function (WK) {
        fetchWorkerIds(WK, '/wishlists/' + encodeURIComponent(publicId) + '/picks?limit=' + Math.min(24, need * 3))
          .then(function (ids) { return resolveWorker(WK, ids); })
          .then(function (products) {
            resolve(products.filter(function (p) { return p.available !== false && keep(p); }).slice(0, need));
          })
          .catch(function () { resolve([]); });
      }, function () { resolve([]); });
    });
  }

  function renderFromIds(root, WK, ids, count) {
    if (!ids.length) { clearOrHide(root); return; }
    resolveWorker(WK, ids).then(function (products) {
      var grid = root.querySelector('.kkwl-recs__grid');
      if (!grid || !products.length) { clearOrHide(root); return; }
      var shown = products.slice(0, count);
      grid.innerHTML = shown.map(recCard).join('');
      paintHearts();
      resolveMoreButton(root, shown);
    });
  }

  function applySharedHeading(root, rawName) {
    var h = root.querySelector('.kkwl-recs__h');
    if (!h) return;
    var tpl = root.getAttribute('data-kkwl-shared-heading');
    if (tpl) {
      var nm = esc(rawName || SHARED_NAME_FALLBACK); // richtext encodes { } as &lcub; &rcub; — match both
      h.innerHTML = tpl.split('&lcub;name&rcub;').join(nm).split('{name}').join(nm);
    }
    h.style.visibility = 'visible';
  }

  function personalize(root) {
    var source = root.getAttribute('data-kkwl-source') || 'wishlist';
    var count = Number(root.getAttribute('data-kkwl-count')) || 4;

    // Anonymous most-wishlisted across the whole store (guest-safe, aggregate).
    if (source === 'trending') {
      if (designMode) return;
      waitForWK(function (WK) {
        fetchWorkerIds(WK, '/wishlists/trending?limit=' + (count * 4)).then(function (ids) {
          renderFromIds(root, WK, ids, count);
        });
      }, function () { clearOrHide(root); });
      return;
    }

    // "Others also saved": products co-saved with this list (shared or own).
    if (source === 'also_saved') {
      if (designMode) return;
      var similar = function (WK, pid) {
        if (!pid) { clearOrHide(root); return; }
        fetchWorkerIds(WK, '/wishlists/' + encodeURIComponent(pid) + '/similar?limit=' + (count * 4)).then(function (ids) {
          renderFromIds(root, WK, ids, count);
        });
      };
      // A shared view: seeded from that list, with the owner-name heading.
      if (window.KkwlBus && window.KkwlBus.sharedId) {
        var sharedId = window.KkwlBus.sharedId;
        var sb = (function () { try { return new URLSearchParams(window.location.search).get('by') || ''; } catch (e) { return ''; } })();
        applySharedHeading(root, sb);
        window.KkwlBus.on('shared', function (d) { if (d && d.name) applySharedHeading(root, d.name); });
        waitForWK(function (WK) { similar(WK, sharedId); }, function () { clearOrHide(root); });
        return;
      }
      // The viewer's own list: only when they have saved something.
      ownList(root, function (items, wl) {
        waitForWK(function (WK) { similar(WK, wl.publicId || ''); }, function () { clearOrHide(root); });
      });
      return;
    }

    // Shared (read-only) view: heading + recommendations are about the list owner.
    if (window.KkwlBus && window.KkwlBus.sharedId) {
      var sharedBy = (function () { try { return new URLSearchParams(window.location.search).get('by') || ''; } catch (e) { return ''; } })();
      applySharedHeading(root, sharedBy); // sync from ?by= (no "based on your loves" flash)
      window.KkwlBus.on('shared', function (d) {
        if (d && d.name) applySharedHeading(root, d.name);
        if (!designMode) seedAndRender(root, count, (d && d.items) || [], window.KkwlBus.sharedId);
      });
      setTimeout(function () { var h = root.querySelector('.kkwl-recs__h'); if (h) h.style.visibility = 'visible'; }, 1500);
      return;
    }

    if (source !== 'wishlist') { clearOrHide(root); return; } // 'collection' mode: server fallback or hide
    if (designMode) { return; }

    // "Based on your wishlist": only for a viewer who has saved something.
    ownList(root, function (items, wl) { seedAndRender(root, count, items, wl.publicId || ''); });
  }

  roots.forEach(personalize);
})();
